import { eulerDegFromQuat, poseAt, qRotate, type Assembly, type Instance, type Vec3 } from './assemblyPose';
import { assemblyOf, gestureTurns, isNext, isPrep, isTool, nextOf, prepOf, prepTurns, type CamFields, type Movement } from './assemblyStore';
import { movementShot, shotCandidates, type HiddenFrom, type Room, type Shot, type ShotSizes } from './movementShot';

// THE SHOT OF THE HARDWARE PREPARED IN THE MOVING PART, played before the part moves (see
// PrepOn.shot): the barrel nuts going into the side rail, framed on the rail where it waits, from
// the side they go in by — as when they were a sub-step of their own. The movement's shot is the
// rail against the frame, from its outer face, at the scale of the chair.
//
// Worked out on the assembly as it stands when the preparation is done: every unit home, the part
// still at the start of its way. `f` with its tool's ride (see useToolRide), never mirrored: the
// mirror is camAt's. `hiddenOf`: how much of the units each side hides, asked of the scene.
//
// `next`: THE SECOND HARDWARE'S instead (see Fitting.nextOn) — the wood dowels going into the rail
// end, at the end of the gesture, on the part fitted onto.
type PrepSizes = { target: Vec3 | null; moving: Vec3 | null; prep: Vec3 | null; tool: Vec3 | null; tool2?: Vec3 | null; fit?: ShotSizes['fit'] };
export function prepShot(
  f: Movement, which: 'prep' | 'next', sizes: PrepSizes, hiddenOf?: (asm: Assembly) => HiddenFrom,
): Promise<CamFields | null> {
  return prepShotWith(movementShot, f, which, sizes, hiddenOf);
}

// AND EVERY VIEW IT WEIGHS (see shotCandidates), for the candidate views under the viewer.
export function prepShotCandidates(
  f: Movement, which: 'prep' | 'next', sizes: PrepSizes, hiddenOf?: (asm: Assembly) => HiddenFrom,
) {
  return prepShotWith(shotCandidates, f, which, sizes, hiddenOf);
}

// THE MOMENT IT IS WORKED OUT ON: the preparation done, or the end of the gesture for the second
// hardware.
export const prepShotAt = (f: Movement, which: 'prep' | 'next') =>
  (which === 'next' ? 1 : prepTurns(f) / gestureTurns(f));

async function prepShotWith<T>(
  take: (m: Shot, sizes?: ShotSizes, hidden?: HiddenFrom, room?: Room) => Promise<T>,
  f: Movement,
  which: 'prep' | 'next',
  sizes: PrepSizes,
  hiddenOf?: (asm: Assembly) => HiddenFrom,
): Promise<T | null> {
  const next = which === 'next';
  if (next ? !nextOf(f) : !prepOf(f) || !prepTurns(f)) return null;
  const asm = assemblyOf({ ...f, mirrorAll: false });
  const done = prepShotAt(f, which);
  const poses = new Map(poseAt(asm, 1, done).map((p) => [p.instance.id, p]));
  const part = poses.get(next ? 'target' : 'moving');
  const mine = next ? isNext : isPrep;
  const units = asm.instances.filter((i) => mine(i.id) && poses.has(i.id));
  if (!part || !units.length) return null;
  // Each unit's way in, turned as the part it goes into stands.
  const wayOf = (i: Instance) => {
    const home = poses.get(i.id)!.pose;
    const on = (i.parent && poses.get(i.parent)?.pose.rot) || [0, 0, 0, 1];
    return {
      seat: [0, 1, 2].map((k) => home.pos[k] - part.pose.pos[k]) as unknown as Vec3,
      spin: eulerDegFromQuat(home.rot),
      axis: qRotate(on, i.axis),
      travel: i.approach,
    };
  };
  const [first, ...rest] = units.map(wayOf);
  const shot = { ...first, targetSeat: part.pose.pos, copies: rest };
  // The tool, where it rides these units (see Fitting.toolOnPrep, toolOnNext); or the second tool,
  // where it rides the second hardware (see Fitting.nextToolId).
  const ride = (next ? f.toolOnNext : f.toolOnPrep) && f.toolRide && sizes.tool ? { size: sizes.tool, at: f.toolRide }
    : next && f.nextToolRide && sizes.tool2 ? { size: sizes.tool2, at: f.nextToolRide } : null;
  const shotSizes: ShotSizes = { target: next ? sizes.target : sizes.moving, moving: sizes.prep, tool: ride, fit: sizes.fit };
  // ZOOM 1 IS THE WHOLE SCENE'S FIT: the part it goes onto, and the moving part at both ends.
  const room = sizes.target && sizes.moving ? [
    { at: f.targetSeat, size: sizes.target },
    { at: [0, 1, 2].map((k) => f.targetSeat[k] + f.seat[k] + f.axis[k] * f.travel) as unknown as Vec3, size: sizes.moving },
    { at: [0, 1, 2].map((k) => f.targetSeat[k] + f.seat[k]) as unknown as Vec3, size: sizes.moving },
  ] : undefined;
  // WHAT HIDES THE UNITS: everything else standing where it is when they go in.
  const still = (id: string): Instance | null => {
    const p = poses.get(id);
    return p ? { ...p.instance, parent: null, seat: p.pose, approach: 0, step: 0, order: 1, hops: undefined, waits: undefined, turn: undefined, enters: undefined } : null;
  };
  const beat: Assembly = {
    instances: [
      ...asm.instances.filter((i) => !mine(i.id) && !isTool(i.id)).map((i) => still(i.id)).filter((i): i is Instance => !!i),
      ...units.map((i, n) => {
        const w = n ? rest[n - 1] : first;
        return { ...i, parent: null, seat: poses.get(i.id)!.pose, axis: w.axis, waits: undefined, turn: undefined, enters: undefined, step: 1, order: 1 };
      }),
    ],
  };
  return take(shot, shotSizes, hiddenOf?.(beat), room);
}
